import React, { useCallback, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Avatar as AvatarIcon } from '@mui/material';
import { Avatar, Loading, Popover } from 'Components';
import { switchAccount } from 'Redux/actions';
import { FormattedMessage } from 'react-intl';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './../header.scss';

const AccountSelector = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const switchingRef = useRef(false);

  const { accounts, user, authId, switchingAccount, switchAccountError } = useSelector(
    state => state.securityRedux
  );
  
  // Check if user logged in via social media
  const isSocialMediaLogin = localStorage.getItem("sm") === "true";

  // Don't show selector if:
  // - User logged in via social media
  // - User has less than 2 accounts
  // - User is not available
  if (isSocialMediaLogin || !accounts || accounts.length < 2 || !user) {
    return null;
  }

  // Find currently selected account
  const currentAccount = accounts.find(acc => acc.authId === authId) || accounts[0];

  const handleAccountChange = useCallback((selectedAccount) => {
    // Prevent duplicate calls
    if (switchingRef.current || switchingAccount) {
      return;
    }
    
    if (selectedAccount && selectedAccount.authId !== authId) {
      switchingRef.current = true;
      const cognitoId = user.cognitoId;
      dispatch(switchAccount(selectedAccount.authId, cognitoId, navigate));
    }
  }, [authId, switchingAccount, user, dispatch, navigate]);

  // Get initials from account name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Get initials for the current user/account
  const getCurrentAccountInitials = () => {
    // If it's a subUser, use composeName
    if (currentAccount.isSubUser && user?.firstName && user?.lastName) {
      return getInitials(`${user.firstName[0]} ${user.lastName[0]}`.toUpperCase());
    }
    
    // Fall back to company title
    if (currentAccount.companyTitle) {
      return getInitials(currentAccount.companyTitle);
    }
    
    // Final fallback
    return getInitials(currentAccount.composeName);
  };

  // Generate color based on account name
  const getAvatarColor = (name) => {
    if (!name) return '#757575';
    const colors = ['#1976d2', '#388e3c', '#d32f2f', '#f57c00', '#7b1fa2', '#0097a7', '#c2185b', '#5d4037'];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const triggerButton = (
    <div className="account-selector-trigger">
      <SwapHorizIcon className="account-selector-icon" />
      <span className="account-selector-text">
        <FormattedMessage id="switch_account" defaultMessage="Switch Account" />
      </span>
    </div>
  );

  return (
    <div className="account-selector-container">
      {switchingAccount && <Loading overlay show full />}
      
      <Popover
        component={triggerButton}
        verticalPosition="bottom"
        horizontalPosition="left"
      >
        <div className="account-selector-popover">
          {/* Current Account Header */}
          <div className="account-selector-header">
            {user?.avatarFileId && user?.avatarFile?.fileKey ? (
              <Avatar
                fileKey={user.avatarFile.fileKey}
                name={user.fullName || user.companyName || currentAccount.composeName}
                width={64}
              />
            ) : (
              <AvatarIcon
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: getAvatarColor(currentAccount.composeName),
                  fontSize: '28px',
                  fontWeight: 500
                }}
              >
                {getCurrentAccountInitials()}
              </AvatarIcon>
            )}
            <div className="account-selector-current-info">
              <div className="account-selector-current-name">{currentAccount.composeName}</div>
              <div className="account-selector-current-email">{currentAccount.email}</div>
            </div>
          </div>

          {/* Divider */}
          <div className="account-selector-divider" />

          {/* Other Accounts */}
          <div className="account-selector-list">
            <div className="account-selector-list-title">
              <FormattedMessage id="other_accounts" defaultMessage="Other Accounts" />
            </div>
            {accounts.map((account) => {
              const isCurrentAccount = account.authId === authId;
              return (
                <div
                  key={account.authId}
                  className={`account-selector-item ${isCurrentAccount ? 'active' : ''} ${switchingAccount ? 'disabled' : ''}`}
                  onClick={() => !switchingAccount && !isCurrentAccount && handleAccountChange(account)}
                >
                  <div className="account-selector-item-info">
                    <div className="account-selector-item-name">{account.composeName}</div>
                    <div className="account-selector-item-email">{account.email}</div>
                  </div>
                  {isCurrentAccount && (
                    <CheckCircleIcon className="account-selector-check" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Error Message */}
          {switchAccountError && (
            <div className="account-selector-error">
              {switchAccountError}
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
};

export default AccountSelector;
